import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { nomenclatureApi, normalizeNomenclatureList } from '@/lib/api'
import { useToast } from './useToast'
import type { CreateNomenclatureDto, UpdateNomenclatureDto } from '@/types'

export function useNomenclature() {
  const qc = useQueryClient()
  const { toast } = useToast()

  const query = useQuery({
    queryKey: ['nomenclature'],
    queryFn: async () => normalizeNomenclatureList((await nomenclatureApi.getAll()).data),
    refetchOnMount: 'always',
  })

  const createMut = useMutation({
    mutationFn: (data: CreateNomenclatureDto) => nomenclatureApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nomenclature'] })
      toast.success('Товар добавлен')
    },
    onError: () => toast.error('Ошибка при добавлении товара'),
  })

  const updateMut = useMutation({
    mutationFn: ({ gtin, data }: { gtin: string; data: UpdateNomenclatureDto }) =>
      nomenclatureApi.update(gtin, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nomenclature'] })
      toast.success('Товар обновлён')
    },
    onError: () => toast.error('Ошибка при обновлении'),
  })

  const deleteMut = useMutation({
    mutationFn: (gtin: string) => nomenclatureApi.delete(gtin),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nomenclature'] })
      toast.success('Товар удалён')
    },
    onError: () => toast.error('Ошибка при удалении'),
  })

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    create: createMut.mutate,
    update: updateMut.mutate,
    remove: deleteMut.mutate,
    isCreating: createMut.isPending,
    isUpdating: updateMut.isPending,
    isDeleting: deleteMut.isPending,
  }
}
